import { useSnapshot } from 'valtio'
import React, { type ReactElement, useState } from 'react'
import { startOfTomorrow } from 'date-fns'
import dateFnsFormat from 'date-fns/format'
import { StandardInput } from '../../../../../../shared/components/ui/forms/StandardInput'
import { countryCodes, usStates } from '../../../../../../shared/utils/addressUtils'
import { toTitleCase } from '../../../../../../shared/utils/formattingUtils'
import UnderlineLabelSection from '../../../../../../shared/components/ui/headings/UnderlineLabelSection'
import { InputFieldType } from '../../../../../../shared/types/InputFieldType'
import { DisabledInput } from '../../../../../../shared/components/ui/forms/DisabledInput'
import { StandardDateInput } from '../../../../../../shared/components/ui/forms/StandardDateInput'
import { BlueToggle } from '../../../../../../shared/components/ui/toggles/BlueToggle'
import TooltipIcon from '../../../../../../shared/components/ui/tooltips/tooltipIcon/TooltipIcon'
import { recurringPaymentEditProxy } from './recurringPaymentEditProxy'
import { handleBlur, handleChange } from './utils'
import { RecurringPaymentEditProxyFormFieldNamesEnum } from './recurringPaymentEditProxyFormFieldNamesEnum'
import { RecurringConfigurationCreditCardDropDown } from './CreditCardDropdown/RecurringConfigurationCreditCardDropDown'

export function RecurringConfigurationEditSlideContents (): ReactElement {
  const {
    customer,
    showAmountError,
    showInvoiceNumberError,
    showStreetAddressOneError,
    showCityError,
    showStateError,
    showPostalCodeError,
    showEmailError,
    showEndDateError,
    showCountryCodeError,
    form:
        {
          values,
          errors
        }
  } = useSnapshot(recurringPaymentEditProxy, { sync: true })

  const formattedTomorrow = dateFnsFormat(startOfTomorrow(), 'yyyy-MM-dd')

  const [stopOnError, setStopOnError] = useState(recurringPaymentEditProxy.stopOnError)

  const handleToggleClick = (): void => {
    recurringPaymentEditProxy.stopOnError = !recurringPaymentEditProxy.stopOnError
    setStopOnError(!stopOnError)
  }

  const tooltipText = 'If a recurring charge fails, the recurring charge will ' +
        'pause until it is resolved and resumed.  Turn this off if you want the recurring charge to continue after failures.'

  return (
    <>
      <UnderlineLabelSection labelText="Payment Details" />

      <div className="flex">
        <StandardInput
          inputType={InputFieldType.Text}
          label="Transaction Amount"
          preLabel="$"
          placeholder="100.00"
          value={values.amount as string || ''}
          onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.Amount)}
          onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.Amount)}
          showError={showAmountError}
          errorMessage={errors.amount as string}
        />

        <StandardInput
          label="Invoice Number"
          value={values.invoiceNumber as string || ''}
          onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.InvoiceNumber)}
          onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.InvoiceNumber)}
          showError={showInvoiceNumberError}
          errorMessage={errors.invoiceNumber as string}
        />
      </div>

      <div className="flex">
        <StandardInput
          label="Notes"
          value={values.notes as string || ''}
          onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.Notes)}
          onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.Notes)}
        />
      </div>

      <UnderlineLabelSection labelText="Customer Details" />

      <div className="mx-2 mb-2 text-blue-500 font-semibold text-lg">
        {customer && `${customer.firstName} ${customer.lastName}  (${customer.companyName})`}
      </div>

      <div className="flex">
        <StandardInput
          label="Service Address"
          value={values.addressOne as string || ''}
          onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.AddressOne)}
          onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.AddressOne)}
          showError={showStreetAddressOneError}
          errorMessage={errors.addressOne as string}
        />
      </div>

      <div className="flex">
        <StandardInput
          label="Service Address 2"
          value={values.addressTwo as string || ''}
          onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.AddressTwo)}
          onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.AddressTwo)}
        />
      </div>

      <div className="flex">
        <StandardInput
          label="City"
          value={values.city as string || ''}
          onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.City)}
          onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.Customer)}
          showError={showCityError}
          errorMessage={errors.city as string}
        />
      </div>

      <div className="flex">

        <div className="flex flex-col w-full mx-2">
          <label>State</label>
          <div className="relative">
            <select
              className=" pt-2 pb-1.5 pl-3 text-left w-full  border border-wpy-gray rounded-md"
              onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.State)}
              onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.State)}
              value={values.state as string || ''}
              required
            >
              <option value="" disabled>Choose State</option>
              {
                  usStates.map(s => (
                    <option key={s.name} value={s.abbreviation}>{toTitleCase(s.name)}</option>)
                  )
              }
            </select>
            {showStateError && (
              <div className="h-5 absolute left-0.5 top-1 text-lg text-red">❗</div>
            )}
          </div>
          <div className="h-6 text-sm text-red-500">{errors.state}</div>
        </div>

        <StandardInput
          label="Zip / Postal Code"
          value={values.postalCode as string || ''}
          onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.PostalCode)}
          onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.PostalCode)}
          showError={showPostalCodeError}
          errorMessage={errors.postalCode as string}
        />

      </div>

      <div className="flex">

        <div className="flex flex-col w-full mx-2">
          <label>Country</label>
          <div className="relative">
            <select
              className="pt-2 pb-1.5 pl-3 text-left w-full border border-wpy-gray rounded-md"
              onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.CountryCode)}
              onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.CountryCode)}
              value={values.countryCode as string || ''}
              required
            >
              <option value="" disabled>Choose Country</option>
              {
                countryCodes.map(s => (
                  <option key={s.name} value={s.abbreviation}>{toTitleCase(s.name)}</option>)
                )
            }
            </select>
            {showCountryCodeError && (
              <div className="h-5 absolute left-2 top-1 text-lg text-red">❗</div>
            )}
          </div>
          <div className="h-6 text-sm text-red-500">{errors.countryCode}</div>
        </div>
      </div>

      <div className="flex">
        <StandardInput
          label="Email"
          value={values.email as string || ''}
          onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.Email)}
          onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.Email)}
          showError={showEmailError}
          errorMessage={errors.email as string}
        />
      </div>

      <UnderlineLabelSection labelText="Recurring Schedule" />

      <div className="flex">
        <DisabledInput
          additionalClasses="mt-1"
          label="Start Date"
          value={values.startDate as string || ''}
        />

        <StandardDateInput
          minDate={formattedTomorrow}
          label="End Date"
          value={values.endDate as string || ''}
          showError={showEndDateError}
          errorMessage={errors.endDate as string}
          onChange={handleChange(RecurringPaymentEditProxyFormFieldNamesEnum.EndDate)}
          onBlur={handleBlur(RecurringPaymentEditProxyFormFieldNamesEnum.EndDate)}
        />
      </div>

      <div className="flex mb-4">
        <DisabledInput
          label="Frequency"
          value={values.billingCycle as string || ''}
        />
      </div>

      <div className="flex flex-row mb-8 ">

        <div className="mr-6 ml-2 mt-1">
          <BlueToggle
            name="stopOnError"
            onClick={handleToggleClick}
            selected={stopOnError}
          />
        </div>
        <div className="flex items-center h-full my-auto text-xl">
          Stop On Error
        </div>
        <TooltipIcon tooltipText={tooltipText} />
      </div>

      <UnderlineLabelSection labelText="Payment Method" />
      <RecurringConfigurationCreditCardDropDown />

    </>
  )
}
